import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Box,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import RemoveIcon from '@material-ui/icons/Remove';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import SendIcon from '@material-ui/icons/Send';
import InboxIcon from '@material-ui/icons/Inbox';
import Autocomplete from '@material-ui/lab/Autocomplete';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ConversationList from '../ConversationList';
import MessageBubble from '../MessageBubble';
import ChatInput from '../ChatInput';
import { apiBaseUrl } from '../../utilities/configureApiFetch';

const COLORS = {
  PRIMARY: '#002D55',
  SECONDARY: '#324158',
  ACCENT: '#fb741a',
  BACKGROUND: '#dce4eb',
  TEXT_PRIMARY: '#002D55',
  TEXT_SECONDARY: '#324158',
  TEXT_TERTIARY: '#666666',
  DIVIDER: '#dce4eb',
  BORDER: '#e0e0e0',
  PLACEHOLDER: '#999999',
  ERROR: '#d32f2f',
  SUCCESS: '#4caf50',
};

const inlineStyles = {
  headerWithBorder: {
    background: COLORS.PRIMARY,
    color: '#fff',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    borderBottom: `3px solid ${COLORS.ACCENT}`,
  },
  headerIconButton: {
    color: '#fff',
    padding: '4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 165, 0, 0.15)',
      borderColor: COLORS.ACCENT,
    },
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
    '& .MuiInputLabel-outlined': {
      color: '#002D55',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
  },
};

const useStyles = makeStyles(() => ({
  customBadge: {
    backgroundColor: '#fb741a',
    color: '#fff',
    boxShadow: '0 0 0 2px rgba(255,255,255,0.6)',
  },
  chatContainer: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 1200,
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'flex-end',
    gap: 8,
  },
  chatPaper: {
    width: 320,
    height: 400,
    borderRadius: 14,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
  },
  chatMessages: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
  },
  chatInputArea: {
    padding: '8px 12px',
    display: 'flex',
    gap: 6,
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  sendButton: {
    color: '#FFFFFF',
    backgroundColor: '#002D55',
    borderRadius: 14,
    border: 'none',
    boxShadow: 'none',
    minWidth: 40,
    textTransform: 'none',
    flexShrink: 0,
    alignSelf: 'stretch',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#b0b8c1',
      color: '#fff',
    },
  },
  minimizedChat: {
    width: 200,
    maxWidth: 220,
    borderRadius: 14,
    padding: '4px 12px',
    background: '#002D55',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    transition: 'box-shadow 120ms ease, transform 120ms ease',
    '&:hover': {
      boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
      transform: 'translateY(-2px)',
    },
    '&:hover $minimizedClose': {
      opacity: 1,
    },
  },
  minimizedClose: {
    opacity: 0,
    transition: 'opacity 160ms ease',
    color: '#fff',
    padding: 6,
  },
  minimizedTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 180,
    cursor: 'pointer',
  },
  chatBubble: {
    padding: '8px 12px',
    minWidth: '70%',
    maxWidth: '70%',
    borderRadius: 14,
  },
  messagesDialog: {
    maxWidth: '45%',
    height: '65vh',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 14,
    overflow: 'hidden',
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
    '& .MuiInputLabel-outlined': {
      color: '#002D55',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
  },
}));

function ChatSystemPatient() {
  const classes = useStyles();
  const [messagesAnchorEl, setMessagesAnchorEl] = useState(null);
  const [openChats, setOpenChats] = useState([]);
  const [minimizedChats, setMinimizedChats] = useState([]);
  const [seenInFullscreen, setSeenInFullscreen] = useState([]);
  const [replyTexts, setReplyTexts] = useState({});
  const [fullscreenChatOpen, setFullscreenChatOpen] = useState(false);
  const [fullscreenChatData, setFullscreenChatData] = useState(null);
  const [availablePatients] = useState([
    { id: 1, user_id: 1, first_name: 'John', last_name: 'Doe', avatar: '🧑' },
    { id: 2, user_id: 2, first_name: 'Jane', last_name: 'Smith', avatar: '👩' },
    { id: 3, user_id: 3, first_name: 'Bob', last_name: 'Johnson', avatar: '🧔' },
  ]);
  const [messages, setMessages] = useState([
    {
      id: 1,
      title: 'Physio: Follow-up',
      unread: true,
      thread: [
        {
          id: 1,
          from: 'physio',
          text: 'Hi — how are you getting on with the exercises?',
          time: '09:15',
        },
      ],
    },
    {
      id: 2,
      title: 'Appointment reminder',
      unread: false,
      thread: [
        {
          id: 1,
          from: 'physio',
          text: 'Reminder: check-in tomorrow at 09:00',
          time: '2025-10-17',
        },
      ],
    },
  ]);

    const handleOpenChat = (message) => {
      const chatId = `chat-${message.id}`;
      const isMinimized = minimizedChats.includes(chatId);
      const hasSeenFullscreen = seenInFullscreen.includes(chatId);
      const chatPayload = {
        id: chatId,
        messageId: message.id,
        title: message.title,
        thread: message,
      };

      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, unread: false } : m))
      );
      handleMessagesClose();

      if (isMinimized) {
        setOpenChats((prev) => (prev.find((c) => c.id === chatId) ? prev : [...prev, chatPayload]));
        setMinimizedChats((prev) => prev.filter((id) => id !== chatId));
        return;
      }

      if (!hasSeenFullscreen) {
        setFullscreenChatData(message);
        setFullscreenChatOpen(true);
        setSeenInFullscreen((prev) => [...prev, chatId]);
        return;
      }

      setOpenChats((prev) => (prev.find((c) => c.id === chatId) ? prev : [...prev, chatPayload]));
    };

    const handleOpenNewMessageDialog = () => {
      const autoSelectPatient =
        availablePatients && availablePatients.length === 1
          ? availablePatients[0]
          : null;
      const newChat = {
        id: Date.now(),
        title: 'New Message',
        isNewMessage: true,
        thread: [],
        selectedPatient: autoSelectPatient,
      };
      setFullscreenChatData(newChat);
      setFullscreenChatOpen(true);
      handleMessagesClose();
    };

    const handleCloseChat = (chatId) => {
      setOpenChats((prev) => prev.filter((c) => c.id !== chatId));
      setMinimizedChats((prev) => prev.filter((id) => id !== chatId));
      setSeenInFullscreen((prev) => prev.filter((id) => id !== chatId));
      setReplyTexts((prev) => {
        const updated = { ...prev };
        delete updated[chatId];
        return updated;
      });
    };

    const handleMinimizeChat = (chatId) => {
      setMinimizedChats((prev) => (prev.includes(chatId) ? prev : [...prev, chatId]));
    };

    const handleSendMessage = (chatId) => {
      const replyText = replyTexts[chatId] || '';
      if (!replyText.trim()) return;

      const openChat = openChats.find((c) => c.id === chatId);
      if (!openChat) return;

      if (openChat.isNewMessage && openChat.selectedPatient) {
        const newThread = {
          id: Date.now(),
          title: `${openChat.selectedPatient.first_name} ${openChat.selectedPatient.last_name}`,
          unread: false,
          thread: [
            {
              id: 1,
              from: 'patient',
              text: replyText.trim(),
              time: new Date().toLocaleTimeString(),
            },
          ],
        };
        setMessages((prev) => [newThread, ...prev]);
        setOpenChats((prev) =>
          prev.map((c) =>
            c.id === chatId
              ? {
                  ...c,
                  messageId: newThread.id,
                  title: newThread.title,
                  thread: newThread,
                  isNewMessage: false,
                }
              : c
          )
        );
      } else if (openChat.messageId) {
        const newMsg = {
          id: Date.now(),
          from: 'patient',
          text: replyText.trim(),
          time: new Date().toLocaleTimeString(),
        };
        setMessages((prev) =>
          prev.map((m) =>
            m.id === openChat.messageId ? { ...m, thread: [...m.thread, newMsg] } : m
          )
        );
      }

      setReplyTexts((prev) => ({ ...prev, [chatId]: '' }));
    };

    const handleOpenFullscreenChat = (chat) => {
      setFullscreenChatData(chat);
      setFullscreenChatOpen(true);
    };

    const handleCloseFullscreenChat = () => {
      if (fullscreenChatData?.id) {
        const chatId = fullscreenChatData.isNewMessage
          ? fullscreenChatData.id
          : `chat-${fullscreenChatData.id}`;

        const chatPayload = fullscreenChatData.isNewMessage
          ? fullscreenChatData
          : {
              id: chatId,
              messageId: fullscreenChatData.id,
              title: fullscreenChatData.title,
              thread: fullscreenChatData,
            };

        setOpenChats((prev) => (prev.find((c) => c.id === chatId) ? prev : [...prev, chatPayload]));
        setMinimizedChats((prev) => (prev.includes(chatId) ? prev : [...prev, chatId]));
      }
      setFullscreenChatOpen(false);
      setFullscreenChatData(null);
    };

    const handleFullscreenClose = () => {
      if (fullscreenChatData?.id) {
        const chatId = fullscreenChatData.isNewMessage
          ? fullscreenChatData.id
          : `chat-${fullscreenChatData.id}`;
        handleCloseChat(chatId);
      }
      setFullscreenChatOpen(false);
      setFullscreenChatData(null);
    };

    const handleSendFullscreenMessage = (replyText) => {
      if (!replyText.trim() || !fullscreenChatData) return;

      if (fullscreenChatData.isNewMessage && fullscreenChatData.selectedPatient) {
        const newThread = {
          id: Date.now(),
          title: `${fullscreenChatData.selectedPatient.first_name} ${fullscreenChatData.selectedPatient.last_name}`,
          unread: false,
          thread: [
            {
              id: 1,
              from: 'patient',
              text: replyText.trim(),
              time: new Date().toLocaleTimeString(),
            },
          ],
        };
        setMessages((prev) => [newThread, ...prev]);
        setFullscreenChatData((prev) => ({
          ...prev,
          messageId: newThread.id,
          title: newThread.title,
          thread: newThread,
          isNewMessage: false,
        }));
      } else if (fullscreenChatData.messageId) {
        const newMsg = {
          id: Date.now(),
          from: 'patient',
          text: replyText.trim(),
          time: new Date().toLocaleTimeString(),
        };
        setMessages((prev) =>
          prev.map((m) =>
            m.id === fullscreenChatData.messageId
              ? { ...m, thread: [...m.thread, newMsg] }
              : m
          )
        );
        setFullscreenChatData((prev) => ({
          ...prev,
          thread: { ...prev.thread, thread: [...prev.thread.thread, newMsg] },
        }));
      }
    };

  const handleMessagesClose = useCallback(() => {
    setMessagesAnchorEl(null);
  }, []);

  const handleOpenFromEvent = useCallback((event) => {
    const anchorEl = event?.detail?.anchorEl || event?.target || null;
    if (anchorEl) {
      setMessagesAnchorEl(anchorEl);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handler = (event) => {
      handleOpenFromEvent(event);
    };

    window.addEventListener('chat:open-messages', handler);
    return () => window.removeEventListener('chat:open-messages', handler);
  }, [handleOpenFromEvent]);

  return (
    <>
        <Menu
          anchorEl={messagesAnchorEl}
          keepMounted
          open={Boolean(messagesAnchorEl)}
          onClose={handleMessagesClose}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ style: { minWidth: 320, maxHeight: 450, borderRadius: 14 } }}
        >
          <div
            style={{
              padding: '12px 16px 4px 16px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 14, color: '#002D55' }}>
              Messages
            </span>
            <Tooltip title="Write new message" arrow>
              <IconButton size="small" style={{ color: '#002D55' }} onClick={handleOpenNewMessageDialog}>
                <SendIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </div>
          <List style={{ maxHeight: 350, overflowY: 'auto', padding: 0 }}>
            {messages.map((msg) => (
              <ListItem
                key={msg.id}
                button
                onClick={() => handleOpenChat(msg)}
                style={{
                  borderBottom: '1px solid #f0f0f0',
                  padding: '6px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <ListItemAvatar>
                    <Avatar style={{ background: '#002D55', color: '#fff', marginRight: 12, width: 32, height: 32, fontSize: '0.8rem' }}>
                      {msg.title.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={msg.title}
                    secondary={msg.thread[msg.thread.length - 1]?.text || ''}
                    primaryTypographyProps={{ style: { fontWeight: msg.unread ? 600 : 400 } }}
                    secondaryTypographyProps={{ noWrap: true, style: { fontSize: '12px' } }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {msg.unread && (
                    <Badge badgeContent=" " variant="dot" classes={{ badge: classes.customBadge }} />
                  )}
                  <Tooltip title="Open fullscreen" arrow>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMessagesClose();
                        handleOpenFullscreenChat(msg);
                      }}
                      style={{ color: '#002D55', padding: '4px' }}
                    >
                      <FullscreenIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              </ListItem>
            ))}
            {messages.length === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
                No messages
              </div>
            )}
          </List>
        </Menu>

        <div className={classes.chatContainer}>
          {openChats
            .filter((chat) => minimizedChats.includes(chat.id))
            .map((chat) => (
              <Paper
                key={chat.id}
                className={classes.minimizedChat}
                elevation={6}
                onClick={() =>
                  chat.isNewMessage
                    ? setMinimizedChats((prev) => prev.filter((id) => id !== chat.id))
                    : handleOpenChat(chat.thread)
                }
              >
                <div className={classes.minimizedTitle}>
                  {chat.title}
                </div>
                <IconButton
                  size="small"
                  className={classes.minimizedClose}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCloseChat(chat.id);
                  }}
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Paper>
            ))}

          {openChats
            .filter((chat) => !minimizedChats.includes(chat.id))
            .map((chat) => {
              const messageThread = messages.find((m) => m.id === chat.messageId);
              const threadMessages = messageThread?.thread || [];

              return (
                <Paper
                  key={chat.id}
                  className={classes.chatPaper}
                  elevation={6}
                >
                  <div style={inlineStyles.headerWithBorder}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {chat.title}
                    </Typography>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Tooltip title="Fullscreen">
                        <IconButton
                          size="small"
                          style={inlineStyles.headerIconButton}
                          onClick={() => handleOpenFullscreenChat(chat.isNewMessage ? chat : chat.thread)}
                        >
                          <FullscreenIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Minimize">
                        <IconButton
                          size="small"
                          style={inlineStyles.headerIconButton}
                          onClick={() => handleMinimizeChat(chat.id)}
                        >
                          <RemoveIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Close">
                        <IconButton
                          size="small"
                          style={inlineStyles.headerIconButton}
                          onClick={() => handleCloseChat(chat.id)}
                        >
                          <CloseIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  {chat.isNewMessage ? (
                    <div
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                      }}
                    >
                      <Typography variant="body2" style={{ color: '#666' }}>
                        Start a new message
                      </Typography>

                      <Autocomplete
                        options={availablePatients}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return `${option.first_name} ${option.last_name}`;
                        }}
                        value={chat.selectedPatient}
                        onChange={(event, newValue) => {
                          setOpenChats((prev) =>
                            prev.map((c) =>
                              c.id === chat.id ? { ...c, selectedPatient: newValue } : c
                            )
                          );
                        }}
                        size="small"
                        renderInput={(params) => (
                          <TextField {...params} label="Patient" variant="outlined" placeholder="Search..." />
                        )}
                        noOptionsText="No patients"
                        renderOption={(option) => (
                          <div style={{ fontSize: '13px' }}>
                            {option.first_name} {option.last_name}
                          </div>
                        )}
                      />
                    </div>
                  ) : (
                    <List className={classes.chatMessages}>
                      {threadMessages.map((m) => (
                        <ListItem
                          key={m.id}
                          style={{ justifyContent: m.from === 'patient' ? 'flex-end' : 'flex-start', padding: '6px 0' }}
                        >
                          <Paper
                            className={classes.chatBubble}
                            style={{
                              background: m.from === 'patient' ? '#f0f0f0' : '#002D55',
                              color: m.from === 'patient' ? '#002D55' : '#fff'
                            }}
                            elevation={0}
                          >
                            <Typography variant="body2" style={{ color: m.from === 'patient' ? '#002D55' : '#fff', fontSize: '13px' }}>
                              {m.text}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{
                                color: '#999',
                                fontSize: '11px',
                                marginTop: '4px',
                                display: 'block',
                                textAlign: m.from === 'patient' ? 'right' : 'left',
                              }}
                            >
                              {m.time}
                            </Typography>
                          </Paper>
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Divider />
                  <div className={classes.chatInputArea}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Message..."
                      value={replyTexts[chat.id] || ''}
                      onChange={(event) =>
                        setReplyTexts((prev) => ({ ...prev, [chat.id]: event.target.value }))
                      }
                      onKeyPress={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          handleSendMessage(chat.id);
                        }
                      }}
                      multiline
                      maxRows={3}
                      InputProps={{ style: { fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)', borderRadius: 14 } }}
                      className={classes.orangeFocused}
                    />
                    <Button
                      variant="contained"
                      className={classes.sendButton}
                      onClick={() => handleSendMessage(chat.id)}
                      disabled={
                        !(replyTexts[chat.id] || '').trim() ||
                        (chat.isNewMessage && !chat.selectedPatient)
                      }
                    >
                      <SendIcon style={{ fontSize: 16 }} />
                    </Button>
                  </div>
                </Paper>
              );
            })}
        </div>

        <Dialog
          open={fullscreenChatOpen}
          onClose={handleCloseFullscreenChat}
          maxWidth="md"
          fullWidth
          style={{ zIndex: 1300 }}
          classes={{ paper: classes.messagesDialog }}
        >
          <DialogTitle disableTypography style={inlineStyles.headerWithBorder}>
            <Typography variant="h6" style={{ flex: 1, fontWeight: 600 }}>
              {fullscreenChatData?.title || 'Message'}
            </Typography>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Tooltip title="Minimize" arrow>
                <IconButton
                  size="small"
                  onClick={handleCloseFullscreenChat}
                  style={{ color: '#fff', padding: '4px' }}
                >
                  <RemoveIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close" arrow>
                <IconButton
                  size="small"
                  onClick={handleFullscreenClose}
                  style={{ color: '#fff', padding: '4px' }}
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </div>
          </DialogTitle>
          <DialogContent
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '90vh',
              maxHeight: '90vh',
              padding: '16px',
            }}
          >
            {fullscreenChatData?.isNewMessage && (
              <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                <Autocomplete
                  size="small"
                  options={availablePatients || []}
                  getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''}`}
                  value={fullscreenChatData?.selectedPatient || null}
                  onChange={(event, patient) => {
                    if (patient && fullscreenChatData?.id) {
                      setFullscreenChatData((prev) => ({ ...prev, selectedPatient: patient }));
                    }
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionId = option?.user_id || option?.id;
                    const valueId = value?.user_id || value?.id;
                    return optionId === valueId;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Care Provider"
                      placeholder="Search care provider..."
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </div>
            )}

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                marginBottom: '16px',
                paddingBottom: '16px',
                minHeight: '200px',
              }}
            >
              {fullscreenChatData?.thread && fullscreenChatData.thread.length > 0 ? (
                fullscreenChatData.thread.map((msg, idx) => (
                  <div key={idx} style={{ marginBottom: '12px' }}>
                    <div
                      style={{
                        textAlign: msg.from === 'me' || msg.sender === 'me' ? 'right' : 'left',
                        marginBottom: '4px',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          maxWidth: '80%',
                          padding: '10px 14px',
                          borderRadius: '18px',
                          backgroundColor:
                            msg.from === 'me' || msg.sender === 'me' ? COLORS.PRIMARY : '#f0f0f0',
                          color:
                            msg.from === 'me' || msg.sender === 'me' ? '#fff' : COLORS.TEXT_PRIMARY,
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg.text}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: COLORS.TEXT_TERTIARY,
                        textAlign: msg.from === 'me' || msg.sender === 'me' ? 'right' : 'left',
                        marginTop: '2px',
                      }}
                    >
                      {(msg.from === 'me' || msg.sender === 'me')
                        ? 'You'
                        : (msg.sender_name || msg.senderName || msg.sender_email || 'Care Provider')}
                      {' • '}{msg.time || moment().format('HH:mm')}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', color: COLORS.PLACEHOLDER, padding: '20px' }}>
                  No messages yet
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', paddingTop: '16px', borderTop: `1px solid ${COLORS.BORDER}` }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type a message..."
                value={replyTexts[fullscreenChatData?.id] || ''}
                onChange={(event) => {
                  if (fullscreenChatData?.id) {
                    setReplyTexts((prev) => ({ ...prev, [fullscreenChatData.id]: event.target.value }));
                  }
                }}
                multiline
                maxRows={3}
                variant="outlined"
                InputProps={{ style: { borderRadius: 14, fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)' } }}
                className={classes.orangeFocused}
              />
              <Button
                variant="contained"
                className={classes.sendButton}
                onClick={() => {
                  const messageText = replyTexts[fullscreenChatData?.id] || '';
                  if (messageText.trim()) {
                    handleSendFullscreenMessage(messageText);
                  }
                }}
              >
                <SendIcon style={{ fontSize: 16 }} />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
  );
}

function ChatSystemApi({ suppressUnreadBadges = false }) {
  const classes = useStyles();
  const history = useHistory();
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const BACKEND_URL = apiBaseUrl;
  const [messagesAnchorEl, setMessagesAnchorEl] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openChats, setOpenChats] = useState([]);
  const [minimizedChats, setMinimizedChats] = useState([]);
  const [seenInFullscreen, setSeenInFullscreen] = useState([]);
  const [replyTexts, setReplyTexts] = useState({});
  const [chatMessages, setChatMessages] = useState({
    'mockup-1': [
      {
        id: 'msg-1',
        sender: 'staff-001',
        recipient: user?.id,
        content: 'Hi! How are your exercises progressing?',
        created_at: new Date(Date.now() - 3600000).toISOString(),
      },
      {
        id: 'msg-2',
        sender: user?.id,
        recipient: 'staff-001',
        content: 'They are going well! I am doing the stretches twice daily.',
        created_at: new Date(Date.now() - 1800000).toISOString(),
      },
      {
        id: 'msg-3',
        sender: 'staff-001',
        recipient: user?.id,
        content: 'That is excellent! Let me know how the exercises go.',
        created_at: new Date().toISOString(),
      },
    ],
    'mockup-2': [
      {
        id: 'msg-4',
        sender: 'staff-002',
        recipient: user?.id,
        content: 'Your progress is excellent!',
        created_at: new Date().toISOString(),
      },
    ],
  });
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [newMessageText, setNewMessageText] = useState('');
  const [newMessageError, setNewMessageError] = useState('');
  const [newMessageSubmitting, setNewMessageSubmitting] = useState(false);
  const [fullscreenChatOpen, setFullscreenChatOpen] = useState(false);
  const [fullscreenChatData, setFullscreenChatData] = useState(null);
  const pollIntervalsRef = useRef({});

  const renderMessageContent = useCallback((content) => {
    const text = String(content || '');
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlPattern);

    return parts.map((part, index) => {
      if (!part) {
        return null;
      }

      if (/^https?:\/\/\S+$/.test(part)) {
        let parsedUrl = null;

        try {
          parsedUrl = new URL(part);
        } catch (error) {
          parsedUrl = null;
        }

        if (parsedUrl && parsedUrl.pathname === '/my_appointments') {
          return (
            <Button
              key={`${part}-${index}`}
              type="button"
              onClick={() => {
                history.push({
                  pathname: parsedUrl.pathname,
                  search: parsedUrl.search,
                  hash: parsedUrl.hash,
                });
              }}
              style={{
                color: '#002D55',
                textDecoration: 'underline',
                textTransform: 'none',
                padding: 0,
                minWidth: 0,
                fontSize: 'inherit',
                lineHeight: 'inherit',
                verticalAlign: 'baseline',
              }}
            >
              Click here to reschedule
            </Button>
          );
        }

        return (
          <a
            key={`${part}-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#002D55', textDecoration: 'underline' }}
          >
            {part}
          </a>
        );
      }

      return <React.Fragment key={`${index}-${part}`}>{part}</React.Fragment>;
    });
  }, [history]);

  const getRecipientId = (profile) =>
    profile?.user_id || profile?.user_id_str || profile?.userId || profile?.id;

  const getRecipientLabel = (profile) => {
    if (!profile) return '';
    const first = profile.first_name || profile.firstName || '';
    const last = profile.last_name || profile.lastName || '';
    const fullName = `${first} ${last}`.trim();
    const careProviderName = profile.care_provider_name || profile.careProviderName || '';
    if (first && careProviderName) {
      return `${first} @ ${careProviderName}`;
    }
    return fullName || profile.email || profile.username || 'Unknown';
  };

  const clearPollInterval = useCallback((conversationId) => {
    if (pollIntervalsRef.current[conversationId]) {
      clearInterval(pollIntervalsRef.current[conversationId]);
      delete pollIntervalsRef.current[conversationId];
    }
  }, []);

  useEffect(() => {
    return () => {
      Object.keys(pollIntervalsRef.current).forEach((conversationId) => {
        clearInterval(pollIntervalsRef.current[conversationId]);
      });
      pollIntervalsRef.current = {};
    };
  }, []);

  const handleMessagesClose = useCallback(() => {
    setMessagesAnchorEl(null);
  }, []);

  const handleOpenFromEvent = useCallback((event) => {
    const anchorEl = event?.detail?.anchorEl || event?.target || null;
    if (anchorEl) {
      setMessagesAnchorEl(anchorEl);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handler = (event) => {
      handleOpenFromEvent(event);
    };

    window.addEventListener('chat:open-messages', handler);
    return () => window.removeEventListener('chat:open-messages', handler);
  }, [handleOpenFromEvent]);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/messaging/conversations/`, {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, token]);

  const fetchRecipients = useCallback(async () => {
    if (!token) return;
    try {
      setRecipientLoading(true);
      const headers = {
        Authorization: `Token ${token}`,
      };
      const currentUserId = user?.id || user?.user_id || user?.userId;
      const isClinician = Boolean(user?.is_staff || user?.admin);

      if (isClinician) {
        const [staffResponse, patientResponse] = await Promise.all([
          fetch(`${BACKEND_URL}/profile/my_careprovider_staff/`, { headers }),
          fetch(`${BACKEND_URL}/treatment/getUserTable/`, { headers }),
        ]);

        const staffPayload = staffResponse.ok ? await staffResponse.json() : { staff: [] };
        const patientPayload = patientResponse.ok ? await patientResponse.json() : [];
        const dedupedRecipients = new Map();

        (staffPayload?.staff || []).forEach((staffMember) => {
          const recipientId = getRecipientId(staffMember);
          if (!recipientId || String(recipientId) === String(currentUserId)) return;
          dedupedRecipients.set(String(recipientId), staffMember);
        });

        (patientPayload || []).forEach((patientProfile) => {
          const recipientId = getRecipientId(patientProfile);
          if (!recipientId || String(recipientId) === String(currentUserId)) return;
          dedupedRecipients.set(String(recipientId), patientProfile);
        });

        setRecipientOptions(Array.from(dedupedRecipients.values()));
        return;
      }

      const staffResponse = await fetch(`${BACKEND_URL}/profile/my_careprovider_staff/`, { headers });
      if (staffResponse.ok) {
        const staffPayload = await staffResponse.json();
        const filteredStaff = (staffPayload?.staff || []).filter((staffMember) => {
          const recipientId = getRecipientId(staffMember);
          return recipientId && String(recipientId) !== String(currentUserId);
        });
        if (filteredStaff.length > 0) {
          setRecipientOptions(filteredStaff);
          return;
        }
      }

      const fallbackResponse = await fetch(`${BACKEND_URL}/profile/my_message_recipient/`, { headers });
      if (fallbackResponse.ok) {
        const fallbackPayload = await fallbackResponse.json();
        const fallbackRecipient = fallbackPayload?.recipient || null;
        const recipientId = getRecipientId(fallbackRecipient);
        setRecipientOptions(
          recipientId && String(recipientId) !== String(currentUserId)
            ? [fallbackRecipient]
            : []
        );
        return;
      }

      setRecipientOptions([]);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      setRecipientOptions([]);
    } finally {
      setRecipientLoading(false);
    }
  }, [BACKEND_URL, token, user]);

  const fetchMessages = useCallback(
    async (conversationId) => {
      if (!token) return null;
      try {
        const response = await fetch(`${BACKEND_URL}/api/messaging/messages/${conversationId}/`, {
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setChatMessages((prev) => ({
            ...prev,
            [conversationId]: data,
          }));
          return data;
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
      return null;
    },
    [BACKEND_URL, token]
  );

  const handleSendMessage = useCallback(
    async (conversationId, content) => {
      if (!token) return;
      try {
        const response = await fetch(`${BACKEND_URL}/api/messaging/messages/${conversationId}/`, {
          method: 'POST',
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content }),
        });
        if (response.ok) {
          const newMessage = await response.json();
          setChatMessages((prev) => ({
            ...prev,
            [conversationId]: [...(prev[conversationId] || []), newMessage],
          }));
          fetchConversations();
        }
      } catch (error) {
        console.error('Error sending message:', error);
      }
    },
    [BACKEND_URL, token, fetchConversations]
  );

  const handleOpenChat = useCallback(
    async (conversation) => {
      const isMinimized = minimizedChats.includes(conversation.id);
      const hasSeenFullscreen = seenInFullscreen.includes(conversation.id);
      
      setMessagesAnchorEl(null);
      setActiveConversationId(conversation.id);

      await fetchMessages(conversation.id);

      if (!pollIntervalsRef.current[conversation.id]) {
        pollIntervalsRef.current[conversation.id] = setInterval(() => {
          fetchMessages(conversation.id);
        }, 5000);
      }

      // If clicking on a minimized chat, restore it to floating mode
      if (isMinimized) {
        setOpenChats((prev) => {
          if (prev.find((c) => c.id === conversation.id)) return prev;
          return [...prev, conversation];
        });
        setMinimizedChats((prev) => prev.filter((id) => id !== conversation.id));
      } else if (!hasSeenFullscreen) {
        // First time opening this chat - open in fullscreen only (don't add to openChats)
        setFullscreenChatData(conversation);
        setFullscreenChatOpen(true);
        setSeenInFullscreen((prev) => [...prev, conversation.id]);
      } else {
        // Already seen in fullscreen before, open as floating
        setOpenChats((prev) => {
          if (prev.find((c) => c.id === conversation.id)) return prev;
          return [...prev, conversation];
        });
      }
    },
    [fetchMessages, minimizedChats, seenInFullscreen]
  );

  const handleCloseChat = useCallback(
    (conversationId) => {
      setOpenChats((prev) => prev.filter((c) => c.id !== conversationId));
      setMinimizedChats((prev) => prev.filter((id) => id !== conversationId));
      setSeenInFullscreen((prev) => prev.filter((id) => id !== conversationId));
      clearPollInterval(conversationId);
    },
    [clearPollInterval]
  );

  const handleMinimizeChat = useCallback(
    (conversationId) => {
      setMinimizedChats((prev) => [...prev, conversationId]);
      clearPollInterval(conversationId);
    },
    [clearPollInterval]
  );

  const handleOpenFullscreenChat = useCallback(
    (chat) => {
      setFullscreenChatData(chat);
      setFullscreenChatOpen(true);
    },
    []
  );

  const handleCloseFullscreenChat = useCallback(() => {
    // When clicking away from fullscreen (backdrop click), minimize to floating chat
    if (fullscreenChatData?.id) {
      // Add to openChats if not already there
      setOpenChats((prev) => {
        if (prev.find((c) => c.id === fullscreenChatData.id)) return prev;
        return [...prev, fullscreenChatData];
      });
      // Mark as minimized
      setMinimizedChats((prev) => [...prev, fullscreenChatData.id]);
    }
    setFullscreenChatOpen(false);
    setFullscreenChatData(null);
  }, [fullscreenChatData]);

  const handleFullscreenClose = useCallback(() => {
    // When clicking the close icon in fullscreen, actually close the chat
    if (fullscreenChatData?.id) {
      handleCloseChat(fullscreenChatData.id);
    }
    setFullscreenChatOpen(false);
    setFullscreenChatData(null);
  }, [fullscreenChatData, handleCloseChat]);

  const handleOpenNewMessage = () => {
    setNewMessageOpen(true);
    setNewMessageError('');
  };

  const handleOpenInbox = () => {
    setInboxOpen(true);
    setMessagesAnchorEl(null);
  };

  const handleCloseInbox = () => {
    setInboxOpen(false);
  };

  const handleCloseNewMessage = () => {
    setNewMessageOpen(false);
    setSelectedRecipient(null);
    setNewMessageText('');
    setNewMessageError('');
  };

  const handleCreateConversation = useCallback(async () => {
    if (!token) return;
    if (!selectedRecipient) {
      setNewMessageError('Select a recipient.');
      return;
    }

    const participantId = getRecipientId(selectedRecipient);
    if (!participantId) {
      setNewMessageError('Selected recipient is missing a user id.');
      return;
    }

    setNewMessageSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/messaging/conversations/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ participant_id: participantId }),
      });

      if (!response.ok) {
        setNewMessageError('Failed to start conversation.');
        return;
      }

      const conversation = await response.json();
      await handleOpenChat(conversation);

      const trimmed = newMessageText.trim();
      if (trimmed) {
        await handleSendMessage(conversation.id, trimmed);
      } else {
        fetchConversations();
      }

      handleCloseNewMessage();
    } catch (error) {
      console.error('Error creating conversation:', error);
      setNewMessageError('Failed to start conversation.');
    } finally {
      setNewMessageSubmitting(false);
    }
  }, [
    BACKEND_URL,
    fetchConversations,
    handleOpenChat,
    handleSendMessage,
    newMessageText,
    selectedRecipient,
    token,
  ]);

  useEffect(() => {
    if (messagesAnchorEl) {
      fetchConversations();
    }
  }, [messagesAnchorEl, fetchConversations]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchConversations();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [fetchConversations]);

  useEffect(() => {
    if (newMessageOpen) {
      fetchRecipients();
    }
  }, [newMessageOpen, fetchRecipients]);

  return (
    <>
      <Menu
        anchorEl={messagesAnchorEl}
        keepMounted
        open={Boolean(messagesAnchorEl)}
        onClose={handleMessagesClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ style: { minWidth: 340, maxHeight: 500, borderRadius: 14 } }}
      >
        <div
          style={{
            padding: '12px 16px 8px 16px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 600,
            color: '#002D55',
          }}
        >
          <span>Messages</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Tooltip title="Inbox" arrow>
              <IconButton size="small" style={{ color: '#002D55' }} onClick={handleOpenInbox}>
                <InboxIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Write new message" arrow>
              <IconButton size="small" style={{ color: '#002D55' }} onClick={handleOpenNewMessage}>
                <SendIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        {conversations && conversations.length > 0 ? (
          <ConversationList
            conversations={conversations}
            loading={loading}
            activeConversationId={activeConversationId}
            onSelectConversation={handleOpenChat}
            suppressUnreadBadges={suppressUnreadBadges}
          />
        ) : (
          <List style={{ maxHeight: 350, overflowY: 'auto', padding: 0 }}>
            <ListItem
              button
              onClick={() => handleOpenChat({ id: 'example-1', other_participant_name: 'Dr. Sarah Johnson' })}
              style={{
                borderBottom: '1px solid #f0f0f0',
                padding: '6px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <ListItemAvatar>
                  <Avatar style={{ background: '#002D55', color: '#fff', marginRight: 12, width: 32, height: 32, fontSize: '0.8rem' }}>
                    D
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Dr. Sarah Johnson"
                  secondary="Example message - click to see chat"
                  primaryTypographyProps={{ style: { fontWeight: 600, fontSize: '0.8rem' } }}
                  secondaryTypographyProps={{ noWrap: true, style: { fontSize: '11px' } }}
                />
              </div>
            </ListItem>
            <ListItem
              button
              onClick={() => handleOpenChat({ id: 'example-2', other_participant_name: 'Physical Therapist Mike' })}
              style={{
                borderBottom: '1px solid #f0f0f0',
                padding: '6px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <ListItemAvatar>
                  <Avatar style={{ background: '#002D55', color: '#fff', marginRight: 12, width: 32, height: 32, fontSize: '0.8rem' }}>
                    P
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Physical Therapist Mike"
                  secondary="Example message - click to see chat"
                  primaryTypographyProps={{ style: { fontWeight: 600, fontSize: '0.8rem' } }}
                  secondaryTypographyProps={{ noWrap: true, style: { fontSize: '11px' } }}
                />
              </div>
            </ListItem>
          </List>
        )}
      </Menu>

      <Dialog
        open={inboxOpen}
        onClose={handleCloseInbox}
        maxWidth="md"
        fullWidth
        style={{ zIndex: 1300 }}
        classes={{ paper: classes.messagesDialog }}
      >
        <DialogTitle disableTypography style={inlineStyles.headerWithBorder}>
          <Typography variant="h6" style={{ flex: 1, fontWeight: 600 }}>
            Inbox
          </Typography>
          <IconButton size="small" onClick={handleCloseInbox} style={{ color: '#fff', padding: 4 }}>
            <CloseIcon style={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '90vh',
            maxHeight: '90vh',
            padding: '16px',
          }}
        >
          {conversations && conversations.length > 0 ? (
            <ConversationList
              conversations={conversations}
              loading={loading}
              activeConversationId={activeConversationId}
              onSelectConversation={(conversation) => {
                handleOpenChat(conversation);
                handleCloseInbox();
              }}
              suppressUnreadBadges={suppressUnreadBadges}
            />
          ) : (
            <List style={{ maxHeight: 350, overflowY: 'auto', padding: 0 }}>
              <ListItem
                button
                onClick={() => {
                  handleOpenChat({ id: 'example-1', other_participant_name: 'Dr. Sarah Johnson' });
                  handleCloseInbox();
                }}
                style={{
                  borderBottom: '1px solid #f0f0f0',
                  padding: '6px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <ListItemAvatar>
                    <Avatar style={{ background: '#002D55', color: '#fff', marginRight: 12, width: 32, height: 32, fontSize: '0.8rem' }}>
                      D
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Dr. Sarah Johnson"
                    secondary="Example message - click to see chat"
                    primaryTypographyProps={{ style: { fontWeight: 600, fontSize: '0.8rem' } }}
                    secondaryTypographyProps={{ noWrap: true, style: { fontSize: '11px' } }}
                  />
                </div>
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  handleOpenChat({ id: 'example-2', other_participant_name: 'Physical Therapist Mike' });
                  handleCloseInbox();
                }}
                style={{
                  borderBottom: '1px solid #f0f0f0',
                  padding: '6px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <ListItemAvatar>
                    <Avatar style={{ background: '#002D55', color: '#fff', marginRight: 12, width: 32, height: 32, fontSize: '0.8rem' }}>
                      P
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Physical Therapist Mike"
                    secondary="Example message - click to see chat"
                    primaryTypographyProps={{ style: { fontWeight: 600, fontSize: '0.8rem' } }}
                    secondaryTypographyProps={{ noWrap: true, style: { fontSize: '11px' } }}
                  />
                </div>
              </ListItem>
            </List>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={newMessageOpen}
        onClose={handleCloseNewMessage}
        maxWidth="md"
        fullWidth
        style={{ zIndex: 1300 }}
        classes={{ paper: classes.messagesDialog }}
      >
        <DialogTitle disableTypography style={inlineStyles.headerWithBorder}>
          <Typography variant="h6" style={{ flex: 1, fontWeight: 600 }}>
            Compose Message
          </Typography>
          <IconButton size="small" onClick={handleCloseNewMessage} style={{ color: '#fff', padding: 4 }}>
            <CloseIcon style={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '90vh',
            maxHeight: '90vh',
            padding: '16px',
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <Autocomplete
              options={recipientOptions}
              loading={recipientLoading}
              value={selectedRecipient}
              onChange={(event, value) => setSelectedRecipient(value)}
              getOptionLabel={getRecipientLabel}
              isOptionEqualToValue={(option, value) =>
                String(getRecipientId(option)) === String(getRecipientId(value))
              }
              renderOption={(option) => (
                <span style={{ fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)' }}>
                  {getRecipientLabel(option)}
                </span>
              )}
              ListboxProps={{ style: { fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)' } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select staff"
                  placeholder="Search..."
                  variant="outlined"
                  size="small"
                  className={classes.orangeFocused}
                  InputProps={{ ...params.InputProps, style: { borderRadius: 14, fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)' } }}
                  inputProps={{ ...params.inputProps, style: { fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)' } }}
                  InputLabelProps={{ ...params.InputLabelProps, style: { fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)' } }}
                />
              )}
            />
          </div>
          <Divider style={{ marginBottom: '16px' }} />

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: '16px',
              paddingBottom: '16px',
              minHeight: '200px',
            }}
          >
            {selectedRecipient ? (
              <div>
                {/* Mockup messages to show styling */}
                <div style={{ marginBottom: '12px' }}>
                  <div
                    style={{
                      textAlign: 'left',
                      marginBottom: '4px',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        maxWidth: '80%',
                        padding: '10px 14px',
                        borderRadius: '18px',
                        backgroundColor: '#f0f0f0',
                        color: COLORS.TEXT_PRIMARY,
                        fontSize: '14px',
                      }}
                    >
                      Hello, how are your exercises going?
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#999',
                      marginLeft: '4px',
                    }}
                  >
                    10:30 AM
                  </span>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div
                    style={{
                      textAlign: 'right',
                      marginBottom: '4px',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        maxWidth: '80%',
                        padding: '10px 14px',
                        borderRadius: '18px',
                        backgroundColor: COLORS.PRIMARY,
                        color: '#fff',
                        fontSize: '14px',
                      }}
                    >
                      They're going well, thanks for asking!
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#999',
                      display: 'block',
                      textAlign: 'right',
                      marginRight: '4px',
                    }}
                  >
                    10:35 AM
                  </span>
                </div>
              </div>
            ) : (
              <Typography variant="body2" style={{ color: '#999', textAlign: 'center', marginTop: '40px' }}>
                Select a patient to start messaging
              </Typography>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', paddingTop: '16px', borderTop: `1px solid ${COLORS.BORDER}` }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={newMessageText}
              onChange={(event) => setNewMessageText(event.target.value)}
              multiline
              maxRows={3}
              variant="outlined"
              InputProps={{ style: { borderRadius: 14, fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)' } }}
              className={classes.orangeFocused}
              disabled={!selectedRecipient}
            />
            <Button
              variant="contained"
              className={classes.sendButton}
              onClick={handleCreateConversation}
              disabled={!selectedRecipient || !newMessageText.trim() || newMessageSubmitting}
              title="Send message"
            >
              <SendIcon style={{ fontSize: 20 }} />
            </Button>
          </div>
          {newMessageError && (
            <Typography variant="body2" style={{ color: COLORS.ERROR, marginTop: '8px' }}>
              {newMessageError}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <div className={classes.chatContainer} style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 8 }}>
        {openChats
          .filter((chat) => minimizedChats.includes(chat.id))
          .map((chat) => (
            <Paper
              key={chat.id}
              className={classes.minimizedChat}
              elevation={6}
              onClick={() => handleOpenChat(chat)}
            >
              <div className={classes.minimizedTitle}>
                {chat.other_participant_name && chat.other_participant_name.length > 15
                  ? `${chat.other_participant_name.slice(0, 15)}...`
                  : chat.other_participant_name}
              </div>
              <IconButton
                size="small"
                className={classes.minimizedClose}
                onClick={(event) => {
                  event.stopPropagation();
                  handleCloseChat(chat.id);
                }}
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Paper>
          ))}

        {openChats
          .filter((chat) => !minimizedChats.includes(chat.id))
          .map((chat) => (
            <Paper
              key={chat.id}
              className={classes.chatPaper}
              elevation={6}
            >
              <div style={inlineStyles.headerWithBorder}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {chat.other_participant_name}
                </Typography>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <Tooltip title="Fullscreen" arrow>
                    <IconButton
                      size="small"
                      style={inlineStyles.headerIconButton}
                      onClick={() => handleOpenFullscreenChat(chat)}
                    >
                      <FullscreenIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Minimize" arrow>
                    <IconButton
                      size="small"
                      style={inlineStyles.headerIconButton}
                      onClick={() => handleMinimizeChat(chat.id)}
                    >
                      <RemoveIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close" arrow>
                    <IconButton
                      size="small"
                      style={inlineStyles.headerIconButton}
                      onClick={() => handleCloseChat(chat.id)}
                    >
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <div className={classes.chatMessages}>
                {chatMessages[chat.id] && chatMessages[chat.id].length > 0 ? (
                  chatMessages[chat.id].map((msg) => {
                    const senderId = msg.sender?.id || msg.sender;
                    const senderLabel = msg.sender_name || msg.senderName || msg.sender_email || 'Care Provider';
                    const initialSource = senderLabel.split('@')[0].trim();
                    const senderInitials = initialSource ? initialSource[0].toUpperCase() : 'U';
                    return (
                      <MessageBubble
                        key={msg.id}
                        message={msg}
                        isSender={String(senderId) === String(user?.id)}
                        senderName={senderLabel}
                        senderInitials={senderInitials}
                      />
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                    No messages yet
                  </div>
                )}
              </div>
              <ChatInput 
                onSendMessage={(content) => {
                  handleSendMessage(chat.id, content);
                  setReplyTexts((prev) => ({ ...prev, [chat.id]: '' }));
                }}
                value={replyTexts[chat.id] || ''}
                onChange={(value) => setReplyTexts((prev) => ({ ...prev, [chat.id]: value }))}
              />
            </Paper>
          ))}
      </div>

      <Dialog
        open={fullscreenChatOpen}
        onClose={handleCloseFullscreenChat}
        maxWidth="md"
        fullWidth
        style={{ zIndex: 1300 }}
        classes={{ paper: classes.messagesDialog }}
        disableBackdropClick={false}
      >
        <DialogTitle disableTypography style={inlineStyles.headerWithBorder}>
          <Typography variant="h6" style={{ flex: 1, fontWeight: 600 }}>
            {fullscreenChatData?.other_participant_name ? (
              <>
                Patient: <span style={{ fontWeight: 400 }}>{fullscreenChatData.other_participant_name}</span>
              </>
            ) : (
              'Message'
            )}
          </Typography>
          <div style={{ display: 'flex', gap: '4px' }}>
            <Tooltip title="Minimize" arrow>
              <IconButton
                size="small"
                onClick={handleCloseFullscreenChat}
                style={{ color: '#fff', padding: '4px' }}
              >
                <RemoveIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close" arrow>
              <IconButton
                size="small"
                onClick={handleFullscreenClose}
                style={{ color: '#fff', padding: '4px' }}
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </div>
        </DialogTitle>
        <DialogContent
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '90vh',
            maxHeight: '90vh',
            padding: '16px',
          }}
        >
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: '16px',
              paddingBottom: '16px',
              minHeight: '200px',
            }}
          >
            {fullscreenChatData && chatMessages[fullscreenChatData.id] && chatMessages[fullscreenChatData.id].length > 0 ? (
              chatMessages[fullscreenChatData.id].map((msg) => {
                const senderId = msg.sender?.id || msg.sender;
                const senderLabel = msg.sender_name || msg.senderName || msg.sender_email || 'Care Provider';
                const initialSource = senderLabel.split('@')[0].trim();
                const senderInitials = initialSource ? initialSource[0].toUpperCase() : 'U';

                return (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isSender={String(senderId) === String(user?.id)}
                    senderName={senderLabel}
                    senderInitials={senderInitials}
                  />
                );
              })
            ) : (
              <div style={{ textAlign: 'center', color: COLORS.PLACEHOLDER, padding: '20px' }}>
                No messages yet
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', paddingTop: '16px', borderTop: `1px solid ${COLORS.BORDER}` }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={replyTexts[fullscreenChatData?.id] || ''}
              onChange={(e) => {
                if (fullscreenChatData?.id) {
                  setReplyTexts((prev) => ({ ...prev, [fullscreenChatData.id]: e.target.value }));
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const trimmed = (replyTexts[fullscreenChatData?.id] || '').trim();
                  if (trimmed && fullscreenChatData?.id) {
                    handleSendMessage(fullscreenChatData.id, trimmed);
                    setReplyTexts((prev) => ({ ...prev, [fullscreenChatData.id]: '' }));
                  }
                }
              }}
              multiline
              maxRows={3}
              variant="outlined"
              InputProps={{ style: { borderRadius: 14, fontSize: 'clamp(0.72rem, 0.8vw, 0.875rem)' } }}
              className={classes.orangeFocused}
            />
            <Button
              variant="contained"
              className={classes.sendButton}
              onClick={() => {
                const trimmed = (replyTexts[fullscreenChatData?.id] || '').trim();
                if (trimmed && fullscreenChatData?.id) {
                  handleSendMessage(fullscreenChatData.id, trimmed);
                  setReplyTexts((prev) => ({ ...prev, [fullscreenChatData.id]: '' }));
                }
              }}
              disabled={!(replyTexts[fullscreenChatData?.id] || '').trim()}
              title="Send message"
            >
              <SendIcon style={{ fontSize: 20 }} />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ChatSystem({ variant = 'api', suppressUnreadBadges = false }) {
  return <ChatSystemApi suppressUnreadBadges={suppressUnreadBadges} />;
}

